import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";

import { notificationsQueryOptions, setNotificationArchived } from "../../api/notifications";
import { notificationTag, RECENT_NOTIFICATION_COUNT } from "../../domain/notifications";
import { isComingSoonPath, SECTION_DOT_CLASS } from "../../domain/sections";
import { formatRelativeTime } from "../../lib/format";
import { showToast } from "../../lib/toast";
import { BellIcon, CloseIcon } from "../icons";
import { NotificationRowLink } from "../links";
import { IconButton } from "../ui/button";
import { Card, cardActionClassName, CardHeader } from "../ui/card";
import { Empty } from "../ui/empty";

/**
 * The top bar's bell: a count of what is unattended, and a panel of the most recent few under it, each one about work
 * marked with how many work items it names. It reads the API's list, which is asked for again every half minute,
 * whenever the Intake page finishes an upload, and when a file that was Uploading is gone from the Intake page's list
 * (a duplicate the copier turned away). It is not a modal dialog — a <dialog> would make the rest of the page inert and
 * swallow the toasts (see src/components/shell/toaster.tsx) — so it closes on Escape and on a press anywhere else, and
 * gives focus back to the bell.
 */
export function NotificationsMenu() {
	const [isOpen, setIsOpen] = useState(false);
	const queryClient = useQueryClient();
	// Until the API answers, the bell shows no count: nothing is known to be unattended yet. The newest unattended page
	// holds more rows than the menu lists (RECENT_NOTIFICATION_COUNT is under NOTIFICATIONS_PAGE_SIZE), and it carries
	// the count of every unattended notification, not only the ones on it.
	const { data: page } = useQuery(notificationsQueryOptions());
	const unattended = page?.total ?? 0;
	const recent = (page?.notifications ?? []).slice(0, RECENT_NOTIFICATION_COUNT);
	const archive = useMutation({
		mutationFn: (id: string) => setNotificationArchived({ id, archived: true }),
		onSuccess: () => {
			showToast("Notification archived.");
		},
		// Either way the list is asked again: a notification that is gone (404) leaves it then.
		onSettled: () => queryClient.invalidateQueries({ queryKey: ["notifications"] }),
	});
	const menuRef = useRef<HTMLDivElement>(null);
	const bellRef = useRef<HTMLButtonElement>(null);

	useEffect(() => {
		if (!isOpen) {
			return;
		}
		const onPointerDown = (event: PointerEvent) => {
			if (event.target instanceof Node && menuRef.current?.contains(event.target) !== true) {
				setIsOpen(false);
			}
		};
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key !== "Escape") {
				return;
			}
			setIsOpen(false);
			bellRef.current?.focus();
		};
		document.addEventListener("pointerdown", onPointerDown);
		document.addEventListener("keydown", onKeyDown);
		return () => {
			document.removeEventListener("pointerdown", onPointerDown);
			document.removeEventListener("keydown", onKeyDown);
		};
	}, [isOpen]);

	return (
		<div ref={menuRef} className="relative">
			<IconButton
				ref={bellRef}
				aria-label={unattended === 0 ? "Notifications" : `Notifications, ${unattended} unattended`}
				aria-expanded={isOpen}
				aria-haspopup="true"
				className="relative"
				onClick={() => {
					setIsOpen(open => !open);
				}}
			>
				<BellIcon />
				{unattended === 0 ? null : (
					<span
						aria-hidden="true"
						className="absolute top-0.5 right-0.5 min-w-[15px] rounded-pill bg-accent px-1 text-center text-micro font-semibold text-bg tabular-nums"
					>
						{unattended}
					</span>
				)}
			</IconButton>
			{isOpen ? (
				<Card className="absolute top-full right-0 z-20 mt-1.5 w-[340px]">
					<CardHeader
						title="Unattended"
						action={
							<Link
								to="/notifications"
								className={cardActionClassName}
								onClick={() => {
									setIsOpen(false);
								}}
							>
								See all →
							</Link>
						}
					/>
					{recent.length > 0 ? (
						<ul aria-label="Recent notifications">
							{recent.map(notification => (
								<NotificationRowLink
									key={notification.id}
									// One about a page still to come opens whole on the Notifications page instead.
									{...(isComingSoonPath(notification.to)
										? { to: "/notifications", search: { notification: notification.id } }
										: { to: notification.to })}
									onClick={() => {
										setIsOpen(false);
									}}
									dotClassName={
										notification.section === null
											? "bg-ink-4"
											: SECTION_DOT_CLASS[notification.section]
									}
									title={notification.title}
									description={notification.description}
									time={formatRelativeTime(notification.at)}
									tag={notificationTag(notification)}
									action={
										<IconButton
											aria-label={`Archive ${notification.title}`}
											disabled={archive.isPending}
											onClick={() => {
												archive.mutate(notification.id);
											}}
										>
											<CloseIcon />
										</IconButton>
									}
								/>
							))}
						</ul>
					) : (
						<Empty>Nothing needs your attention.</Empty>
					)}
				</Card>
			) : null}
		</div>
	);
}
